import React, { useCallback, useMemo } from "react";
import { Feature, Point } from "geojson";
import { FeatureIdentifier } from "mapbox-gl";

import { inAddress } from "fond/layers";
import { pointDrawingConfig } from "fond/map/Toolbar";
import { useAppSelector } from "fond/utils/hooks";

import DrawHandler from "../DrawHandler";

import addressDrawStyles from "./addressDrawStyles";

const defaultOptions = {
  snap: false,
};

const sources: FeatureIdentifier[] = [{ source: inAddress }];

// Base drawing point tools without snapping
const toolbarConfig = pointDrawingConfig.filter(({ id }) => id !== "snappingTools");

/**
 * This component is responsible for managing the drawing of demand points.
 * It uses the Mapbox Draw library to provide drawing functionality and
 * manages the state of the drawn features.
 */
const AddressHandler: React.FC = () => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);

  // The initial features are derived from the data layer for addresses which
  // are drawn as Points.
  const initialFeatures: Feature<Point>[] = useMemo(() => (data?.layers[inAddress].features || []) as Feature<Point>[], [data]);

  /**
   * Determines the initial properties for any new feature being drawn.
   */
  const getFeatureProperties = useCallback((_: Feature) => ({ NumFibers: 1, SubTypes: null, Type: "T1_DEMAND", layerId: inAddress }), []);

  return (
    <DrawHandler
      startingMode={initialFeatures.length > 0 ? "simple_select" : "draw_point"}
      initialFeatures={initialFeatures}
      sources={sources}
      styles={addressDrawStyles}
      config={toolbarConfig}
      getFeatureProperties={getFeatureProperties}
      options={defaultOptions}
    />
  );
};

export default AddressHandler;
